import React, { useEffect, useMemo, useState } from 'react'
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material'
import { Steps, Tabs } from 'antd'
import { toast } from 'react-toastify'
import {
  getPlano, getPlanoGrupos, completarPlanoMasivo, publicarPlano, rechazarPlano,
  getListas,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import PlanoWorkbench from './PlanoWorkbench'
import { columnasBase, colAcciones, chip } from './columnasPrecios'

/** Mes del ciclo (la decisión del comité es mensual). */
const mesActual = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Descripción sugerida: lo que SAP ya sabe del material (modelo + color + año). */
const descripSugerida = (row) =>
  [row.modelo_sap || row.modelo_corto, row.color_sap, row.anio_modelo || '']
    .filter((x) => x && String(x).trim() !== '')
    .join(' ')
    .trim()

/**
 * Junta las filas por MOTO. El plano vive por material —una fila por color— porque así lo pide SAP, pero
 * las decisiones (completar, publicar, devolver) son del modelo: los colores van juntos. Los campos salen
 * con los mismos nombres que usa la bandeja agrupada del back, para que las columnas base rendericen igual.
 */
const agruparPorMoto = (filas) => {
  const porMoto = new Map()
  filas.forEach((r) => {
    const clave = String(r.grupo_id || `${r.modelo_sap}|${r.anio_modelo}`)
    if (!porMoto.has(clave)) {
      porMoto.set(clave, {
        clave,
        grupo_id: Number(r.grupo_id || 0),
        marca: r.marca,
        modelo_sap: r.modelo_sap,
        anio_modelo: r.anio_modelo,
        zona: r.zona,
        pvp: r.pvp,
        iva_pct: r.iva_pct,
        lineas: [],
      })
    }
    porMoto.get(clave).lineas.push(r)
  })
  return [...porMoto.values()].map((m) => ({
    ...m,
    materiales_texto: m.lineas.map((l) => l.material).join(', '),
    colores_texto: m.lineas.map((l) => l.color_sap).filter(Boolean).join(', '),
  }))
}

/** El orden de la cascada, en un solo lugar: lo usan el Steps de arriba y los tabs de abajo. */
const ORDEN = ['pend', 'armar', 'comp', 'pub']

const BackComercialTab = ({ onChanged }) => {
  // bandeja por MOTO (lo que mandó Trade) + el detalle por color de las que ya están listas
  const [motos, setMotos] = useState([])
  const [motosCompletas, setMotosCompletas] = useState([])
  const [completos, setCompletos] = useState([])
  const [publicados, setPublicados] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  // destinatarios 802 tomados del catálogo de listas (no hardcodeados)
  const [dest802, setDest802] = useState({ leticia: '', sanAndres: '' })
  // cascada: sub-paso activo + motos que vienen preseleccionadas desde la bandeja (una o varias)
  const [paso, setPaso] = useState('pend')
  const [preGrupoIds, setPreGrupoIds] = useState([])
  // devolver a Trade
  const [rechazo, setRechazo] = useState({ open: false, fila: null, moto: null, motivo: '' })
  // la excepción: ver los colores de una moto lista, uno por uno
  const [verListas, setVerListas] = useState(null)

  /** De la bandeja al armado del plano de ESA moto: es el mismo trámite, no otra pantalla. */
  const irACalcular = (row) => {
    if (!row.grupo_id) {
      toast.warn('Ese material todavía no está vinculado a un modelo.')
      return
    }
    setPreGrupoIds([Number(row.grupo_id)])
    setPaso('armar')
  }

  /** Varias motos tildadas en la bandeja: se arma el plano de todas juntas. */
  const irACalcularVarias = (filas) => {
    const ids = [...new Set(filas.map((r) => Number(r.grupo_id)).filter((x) => x > 0))]
    if (!ids.length) {
      toast.warn('Marca al menos una moto vinculada a un modelo.')
      return
    }
    if (ids.length < filas.length) {
      toast.info(`${filas.length - ids.length} fila(s) no están vinculadas a un modelo y quedaron afuera.`)
    }
    setPreGrupoIds(ids)
    setPaso('armar')
  }

  /** Recarga todas las bandejas. Devuelve las líneas pendientes frescas para que el auto-completar
      no lea un estado viejo justo después de calcular. */
  const fetchAll = async () => {
    setIsLoading(true)
    const [g, gc, p, c, pub, l] = await Promise.all([
      getPlanoGrupos('pendiente'), getPlanoGrupos('completo'),
      getPlano('pendiente'), getPlano('completo'), getPlano('publicado'),
      getListas(),
    ])
    if (g && g.success) setMotos(g.data)
    if (gc && gc.success) setMotosCompletas(gc.data)
    if (c && c.success) setCompletos(c.data)
    if (pub && pub.success) setPublicados(pub.data)
    // Los códigos de destinatario de la tabla 802 salen del catálogo de condiciones SAP
    if (l && l.data) {
      const d802 = l.data.filter((x) => x.tabla_sap === '802' && x.destinatario)
      setDest802({
        leticia: (d802.find((x) => (x.clave || '').includes('LETICIA')) || {}).destinatario || '',
        sanAndres: (d802.find((x) => (x.clave || '').includes('SANANDRES')) || {}).destinatario || '',
      })
    }
    setIsLoading(false)
    return (p && p.success) ? p.data : []
  }

  useEffect(() => {
    fetchAll()
  }, [])

  /**
   * Lo que el sistema YA SABE del material: el modelo del catálogo SAP, la descripción (modelo + color +
   * año), la zona del conca, el mes del ciclo y los destinatarios 802 del catálogo de condiciones.
   * Devuelve los campos y la lista de lo que se autollenó. Lo usa el auto-completar del armado.
   */
  const prellenado = (row) => {
    const auto = []
    const modelo = row.modelo_corto || row.modelo_sap || ''
    if (!row.modelo_corto && row.modelo_sap) auto.push('Modelo (del catálogo SAP)')

    const descrip = row.descrip || descripSugerida(row)
    if (!row.descrip && descrip) auto.push('Descripción (modelo + color + año)')

    const mes = row.mes || mesActual()
    if (!row.mes) auto.push('Mes (ciclo actual)')

    const dl = row.dest_leticia || dest802.leticia
    const ds = row.dest_san_andres || dest802.sanAndres
    if (!row.dest_leticia && dl) auto.push('Destinatarios 802 Leticia / San Andrés')

    return {
      auto,
      campos: {
        modelo_corto: modelo,
        modelo_precios: row.modelo_precios || modelo,
        descrip,
        zona: row.zona || 'TODAS',
        organizacion: row.organizacion || '1000',
        mes,
        dest_leticia: dl,
        dest_san_andres: ds,
      },
    }
  }

  const publicar = async (id) => {
    const res = await publicarPlano({ id: String(id) })
    if (res && res.success && res.id > 0) {
      toast.success('Plano publicado: recién ahora el precio queda puesto.')
      fetchAll()
      onChanged?.()
    } else {
      toast.error('No se pudo publicar.')
    }
  }

  /**
   * Publicar la moto entera. El back publica una línea por llamada, así que van las N juntas y se cuenta:
   * si alguna falla, las otras SÍ quedaron publicadas y se dice cuántas — no se finge todo o nada.
   */
  const publicarMoto = async (moto) => {
    if (!moto || !moto.lineas.length) return
    const res = await Promise.all(moto.lineas.map((l) => publicarPlano({ id: String(l.id) })))
    const ok = res.filter((r) => r && r.success && r.id > 0).length
    if (!ok) {
      toast.error('No se pudo publicar.')
      return
    }
    if (ok === moto.lineas.length) {
      toast.success(`${ok} color(es) publicados: recién ahora el precio queda puesto.`)
    } else {
      toast.warn(`Se publicaron ${ok} de ${moto.lineas.length}. Los que faltan siguen en el paso 3.`)
    }
    setVerListas(null)
    fetchAll()
    onChanged?.()
  }

  /** Devolver a Trade. No revierte nada: la propuesta nunca se aplicó. */
  const devolver = async () => {
    if (!rechazo.motivo.trim()) {
      toast.warn('Escribe por qué se devuelve.')
      return
    }
    const motivo = rechazo.motivo.trim()
    // una línea o la moto entera: la decisión es la misma, cambia a cuántos colores se aplica
    const filas = rechazo.moto ? rechazo.moto.lineas : [rechazo.fila]
    const res = await Promise.all(filas.map((f) => rechazarPlano(f.id, motivo)))
    const ok = res.filter((r) => {
      const d = (r && r.data && r.data[0]) || null
      return d && Number(d.result) > 0
    }).length
    if (!ok) {
      toast.error('No se pudo devolver el plano.')
      return
    }
    if (ok === filas.length) {
      toast.success(`Devuelto a Trade con el motivo: ${ok} color(es).`)
    } else {
      toast.warn(`Se devolvieron ${ok} de ${filas.length}. El resto sigue acá.`)
    }
    setRechazo({ open: false, fila: null, moto: null, motivo: '' })
    setVerListas(null)
    fetchAll()
    onChanged?.()
  }

  /** Bandeja: una fila por MOTO. Antes eran 12 filas para una moto de 12 colores. */
  const colsMotos = useMemo(
    () => [
      ...columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'zona', 'pvp', 'iva'], {
        color: { header: 'Colores' },
        pvp: { header: 'PVP propuesto' },
      }),
      {
        id: 'estado',
        header: 'Estado',
        size: 190,
        accessorFn: (r) => `${r.pendientes} pendientes`,
        Cell: ({ row }) => {
          const r = row.original
          const calc = Number(r.lineas_calculadas || 0)
          return (
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {chip(`${r.pendientes} por armar`, '#fff4dd', '#7a5300')}
              {calc > 0
                ? <span style={{ fontSize: 11, color: '#1f9d55' }}>{calc} líneas calculadas</span>
                : <span style={{ fontSize: 11, color: '#9aa5b1' }}>sin calcular</span>}
            </span>
          )
        },
      },
      { accessorKey: 'ultimo_movimiento', header: 'Lo mandó Trade', size: 130 },
      colAcciones((r) => (
        <Button size="small" variant="contained" onClick={() => irACalcular(r)}>
          Armar plano
        </Button>
      ), 140),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /** Paso 4 por MOTO: dar 12 veces Publicar no son 12 decisiones, es la misma decisión repetida. */
  const motosListas = useMemo(() => agruparPorMoto(completos), [completos])

  /**
   * Auto-completa las motos cuyos datos YA se derivan del catálogo: copia el modelo del maestro y arma la
   * descripción como modelo + color + año. Ese dato el sistema ya lo sabe, así que no hay paso de completar
   * a mano: se dispara solo al continuar desde el armado. El portón antes de SAP sigue siendo publicar.
   * Idempotente: si no queda nada derivable, no completa nada.
   */
  const autocompletarDerivables = async (pend) => {
    const items = []
    let saltadas = 0
    agruparPorMoto(pend).forEach((m) => {
      const lineas = m.lineas.map((l) => ({ l, pre: prellenado(l) }))
      const completa = lineas.every(({ pre }) => pre.campos.modelo_corto && pre.campos.descrip)
      if (!completa) { saltadas += 1; return }
      lineas.forEach(({ l, pre }) => items.push({ material: l.material, ...pre.campos }))
    })
    if (!items.length) {
      if (saltadas) {
        toast.info(`${saltadas} moto(s) no se pudieron completar solas: devuélvelas a Trade para corregir.`)
      }
      return
    }
    const res = await completarPlanoMasivo(items)
    if (!res || !res.success) {
      toast.error('No se pudieron completar los planos.')
      return
    }
    const ok = Number(res.procesadas || 0)
    const cola = saltadas ? ` Quedan ${saltadas} moto(s) sin completar: devuélvelas a Trade.` : ''
    if (ok === items.length) toast.success(`${ok} línea(s) completadas automáticamente.${cola}`)
    else toast.warn(`${ok} de ${items.length} completadas.${cola}`)
  }

  /**
   * Cierre del armado: ya calculado el plano, se auto-completan las líneas derivables (modelo del maestro
   * + descripción modelo + color + año) y la moto pasa directo a Listos para publicar. Ya no hay paso de
   * completar a mano; el color equivocado se maneja devolviendo a Trade desde Listos. Se relee la bandeja
   * pendiente fresca antes de completar, porque el fetchAll del onChanged es async y llega tarde.
   */
  const continuarACompletar = async () => {
    const pend = await fetchAll()
    await autocompletarDerivables(pend)
    await fetchAll()
    setPaso('comp')
  }

  /** Una fila por moto lista: publicar todos sus colores de una, o devolver la moto entera. */
  const colsMotosListas = useMemo(
    () => [
      ...columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'zona', 'pvp'], {
        material: { header: 'Materiales' },
        modelo: { header: 'Modelo (SAP)' },
        color: { header: 'Colores' },
      }),
      colAcciones((r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          <Button size="small" variant="contained" onClick={() => publicarMoto(r)}>
            {r.lineas.length === 1 ? 'Publicar' : `Publicar los ${r.lineas.length} colores`}
          </Button>
          <Button size="small" variant="text" onClick={() => setVerListas(r)}>
            Ver uno por uno
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={() => setRechazo({ open: true, fila: null, moto: r, motivo: '' })}
          >
            Devolver
          </Button>
        </span>
      ), 330),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const colsComp = useMemo(
    () => [
      ...columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'zona', 'pvp'], {
        modelo: { header: 'Modelo' },
      }),
      colAcciones((r) => (
        <span style={{ display: 'flex', gap: 6 }}>
          <Button size="small" variant="contained" color="primary" onClick={() => publicar(r.id)}>
            Publicar
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={() => setRechazo({ open: true, fila: r, moto: null, motivo: '' })}
          >
            Devolver
          </Button>
        </span>
      ), 210),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const colsPub = useMemo(
    () => [
      ...columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'zona', 'pvp'], {
        modelo: { header: 'Modelo' },
      }),
      { accessorKey: 'fecha_publicado', header: 'Publicado', size: 130 },
    ],
    [],
  )

  const tabla = (cols, data, extraProps = {}) => (
    <TablaPrecios columns={cols} data={data} isLoading={isLoading} pageSize={10} {...extraProps} />
  )

  return (
    <div>
      {/* El paso a paso, visible y único navegador: dónde vas, qué falta y qué sigue.
          Ya no hay paso de completar: al calcular, el plano se auto-completa con lo que el catálogo ya
          sabe y la moto pasa directo a Listos. El Steps dispara setPaso; los paneles de abajo van sin
          barra de tabs (renderTabBar null). */}
      <div className="precios-pasos">
        <Steps
          size="small"
          current={Math.max(0, ORDEN.indexOf(paso))}
          onChange={(i) => setPaso(ORDEN[i])}
          items={[
            {
              title: 'Motos que mandó Trade',
              description: motos.length ? `${motos.length} esperando` : 'nada nuevo',
            },
            { title: 'Armar el plano', description: 'calcula los precios de cada lista' },
            {
              title: 'Listos para publicar',
              description: motosListas.length ? `${motosListas.length} moto(s)` : 'nada listo',
            },
            { title: 'Publicados', description: `${publicados.length} este ciclo` },
          ]}
        />
      </div>

      <Tabs
        activeKey={paso}
        onChange={setPaso}
        renderTabBar={() => null}
        items={[
          {
            key: 'pend',
            label: `Motos que mandó Trade (${motos.length})`,
            children: (
              <div>
                <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 10 }}>
                  Una fila por <b>moto</b>; sus colores van juntos. Solo propuestas de Trade sin publicar.
                </div>
                {tabla(colsMotos, motos, {
                  // Tildar varias y armar el plano de todas: los márgenes se ponen en una grilla.
                  enableRowSelection: (row) => Number(row.original.grupo_id) > 0,
                  getRowId: (r, i) => (Number(r.grupo_id) > 0 ? `g${r.grupo_id}` : `i${i}`),
                  renderTopToolbarCustomActions: ({ table }) => {
                    const filas = table.getSelectedRowModel().rows.map((r) => r.original)
                    return (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!filas.length}
                        onClick={() => irACalcularVarias(filas)}
                      >
                        Armar plano ({filas.length})
                      </Button>
                    )
                  },
                })}
              </div>
            ),
          },
          {
            key: 'armar',
            label: 'Armar el plano',
            children: (
              <PlanoWorkbench
                /* Al calcular, la bandeja de ESTE tab también se refresca: si no, "Continuar" busca la
                   moto en una lista vieja y contesta "no tiene materiales pendientes" cuando sí tiene. */
                onChanged={() => { fetchAll(); onChanged?.() }}
                preGrupoIds={preGrupoIds}
                onContinuar={continuarACompletar}
                /* solo las motos pendientes por publicar: las que Trade mandó (pendiente) y las que
                   ya están completas pero todavía no se publicaron */
                soloGrupos={[
                  ...motos.map((m) => Number(m.grupo_id)),
                  ...motosCompletas.map((m) => Number(m.grupo_id)),
                ]}
              />
            ),
          },
          {
            key: 'comp',
            label: `Listos para publicar (${motosListas.length})`,
            children: (
              <div>
                <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 10 }}>
                  <b>Publicar es lo único que mueve el precio.</b> Los colores de cada moto se publican juntos.
                </div>
                {tabla(colsMotosListas, motosListas)}
                {verListas && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2439' }}>
                        {verListas.marca} {verListas.modelo_sap} {verListas.anio_modelo} — color por color
                      </span>
                      <span style={{ fontSize: 11.5, color: '#6b7684' }}>
                        para la excepción: publicar o devolver un color suelto
                      </span>
                      <Button size="small" onClick={() => setVerListas(null)}>Ocultar</Button>
                    </div>
                    {tabla(colsComp, verListas.lineas)}
                  </div>
                )}
              </div>
            ),
          },
          { key: 'pub', label: `Publicados (${publicados.length})`, children: tabla(colsPub, publicados) },
        ]}
      />

      {/* Devolver a Trade */}
      <Dialog
        open={rechazo.open}
        onClose={() => setRechazo({ open: false, fila: null, moto: null, motivo: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Devolver a Trade —{' '}
          {rechazo.moto
            ? `${[rechazo.moto.marca, rechazo.moto.modelo_sap, rechazo.moto.anio_modelo].filter(Boolean).join(' ')} (${rechazo.moto.lineas.length} color(es))`
            : rechazo.fila && rechazo.fila.material}
        </DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 10 }}>
            El precio propuesto <b>nunca se aplicó</b>, así que devolver no revierte nada: la moto vuelve a
            Trade con tu motivo y no se carga a SAP hasta que la corrijan.
          </div>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="¿Por qué se devuelve? *"
            placeholder="Ej: el PVP no cuadra con el acta del comité; falta el bono"
            value={rechazo.motivo}
            onChange={(e) => setRechazo({ ...rechazo, motivo: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRechazo({ open: false, fila: null, moto: null, motivo: '' })}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={devolver}>Devolver</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default BackComercialTab
